import { zodResolver } from '@hookform/resolvers/zod';
import pick from 'lodash/pick';
import { RateLimitUnit } from 'modules/workflows/constants';
import { CallWebhookNodeData } from 'modules/workflows/types';
import { SECURE_CREDENTIAL_VALUE } from 'modules/workflowWebhooks/constants';
import { FC, PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { isValidUrlWithHandlebars } from 'utils/validation';
import { z } from 'zod';
import { EditorBaseProps } from '../../types';
import {
  AUTH_TOKEN_FIELD,
  CHECKBOX_VAL,
  DEFAULT_RATE_LIMIT_PER_MINUTE,
  ENDPOINT_FIELD,
  EXISTING_AUTH_TOKEN,
  MIN_RATE_LIMITS,
  NODE_DATA_FIELDS,
  PERIOD_FIELD,
  PERMIT_FIELD,
  RADIO_VAL,
  RESOURCE_FIELD,
  VALIDATION_MESSAGES,
  WEBHOOK_FIELD,
} from './CallWebhookEditor.constants';

const schema = z.object({
  [RADIO_VAL]: z.string(),
  [CHECKBOX_VAL]: z.boolean(),
  nodeData: z
    .object({
      [WEBHOOK_FIELD]: z.number().nullish(),
      [RESOURCE_FIELD]: z.string().nullish(),
      [ENDPOINT_FIELD]: z
        .string()
        .refine((val) => isValidUrlWithHandlebars(val).length === 0, {
          message: VALIDATION_MESSAGES[ENDPOINT_FIELD],
        })
        .nullish(),
      [AUTH_TOKEN_FIELD]: z.string().nullish(),
      [PERMIT_FIELD]: z.coerce.number().optional(),
      [PERIOD_FIELD]: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const permitField = data[PERMIT_FIELD];
      const periodField = data[PERIOD_FIELD] as RateLimitUnit;
      const rateLimitMin = periodField && MIN_RATE_LIMITS[periodField];

      if ((!!permitField && permitField < rateLimitMin) || permitField === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${VALIDATION_MESSAGES[PERMIT_FIELD]} ${rateLimitMin}.`,
          path: [PERMIT_FIELD],
        });
      }
    }),
});

const getSchema = (clonedFromNodeData?: Partial<CallWebhookNodeData>) =>
  schema.superRefine((data, ctx) => {
    if (clonedFromNodeData?.[WEBHOOK_FIELD] && !data?.nodeData?.[WEBHOOK_FIELD]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: VALIDATION_MESSAGES.CLONE,
        path: [`nodeData.${WEBHOOK_FIELD}`],
      });
    }
  });

export type CallWorkflowEditorProps = EditorBaseProps<CallWebhookNodeData>;

export type CallWebhookEditorFormSchemaType = Required<z.infer<typeof schema>>;

/**
 * This form provider is created using [React Hook Form](https://react-hook-form.com/)
 * and [zod](https://zod.dev/) for schema validation
 */
export const CallWebhookFormProvider: FC<PropsWithChildren<CallWorkflowEditorProps>> = ({
  clonedFromNodeData,
  nodeData,
  children,
}) => {
  const defaultValues = {
    [CHECKBOX_VAL]: !!nodeData.permit,
    [RADIO_VAL]: nodeData.endpoint ? ENDPOINT_FIELD : WEBHOOK_FIELD,
    nodeData: {
      ...pick(nodeData, NODE_DATA_FIELDS),
      [RESOURCE_FIELD]: nodeData[RESOURCE_FIELD]?.name,
      [PERMIT_FIELD]: nodeData[PERMIT_FIELD] || DEFAULT_RATE_LIMIT_PER_MINUTE,
      [PERIOD_FIELD]: nodeData[PERIOD_FIELD] || RateLimitUnit.Minute,
      [AUTH_TOKEN_FIELD]:
        AUTH_TOKEN_FIELD in nodeData
          ? nodeData[AUTH_TOKEN_FIELD]
          : nodeData[EXISTING_AUTH_TOKEN]
            ? SECURE_CREDENTIAL_VALUE
            : null,
    },
  };

  const methods = useForm<CallWebhookEditorFormSchemaType>({
    defaultValues,
    mode: 'all',
    resolver: zodResolver(getSchema(clonedFromNodeData)),
    shouldUnregister: true,
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};
